/**
 * AnalyticsService
 * Integrates Google Analytics 4 (GA4) for user engagement tracking
 * while respecting therapeutic data privacy.
 *
 * Architecture:
 * - Wraps gtag() for safe GA4 event tracking
 * - Listens to eventBus events and forwards anonymized data to GA4
 * - NEVER tracks message content, questionnaire answers, or PII
 * - Tracks engagement metrics: session duration, message counts, mode switches
 *
 * Privacy Rules (CRITICAL):
 * NEVER Track:
 * - Message content or AI responses
 * - Questionnaire answers or scores
 * - User names, email, PII
 * - Insight content
 * - Clinical scores (PHQ-9, GAD-7)
 *
 * Safe to Track:
 * - Counts (messages, sessions)
 * - Durations (session length)
 * - Identifiers (path IDs, mode names)
 * - User type (guest/authenticated)
 * - Subscription tier
 * - Error types (not details)
 *
 * Usage:
 *   const service = new AnalyticsService(eventBus, config);
 *   service.initialize();
 */

import { analyticsTierFromStatus, getSubscriptionSnapshot } from '../core/subscriptionStatus.js';

/**
 * The GA4 `subscription_tier` value for a user, snapshot-first.
 *
 * The shared snapshot is refetched, so it decides whenever it exists. The
 * identity response's `user.subscriptionTier` ('free'|'premium') is a
 * boot-time stamp that never refreshes — it serves only until the first
 * snapshot lands.
 *
 * @param {Object|null} [user] - User object from the identity response
 * @returns {'free'|'premium'} Tier value
 */
export function resolveAnalyticsTier(user) {
  const snapshot = getSubscriptionSnapshot();
  if (snapshot) return analyticsTierFromStatus(snapshot);
  return user?.subscriptionTier || 'free';
}

/**
 * Map a checkout failure's raw error message to a bounded GA4 `error_type`.
 *
 * The `upgrade:checkout-failed` payload carries the raw message (unchanged —
 * other consumers may want it); the subscriber classifies here so raw error
 * text never reaches GA4. Buckets: transport problems (`network`), a
 * misconfigured/unreachable Stripe setup (`stripe_config`), a 409-family
 * conflict — already subscribed, complimentary Premium, checkout in progress
 * (`checkout_conflict`) — and everything else (`unknown`).
 *
 * @param {string} [message] - Raw error message from the checkout attempt
 * @returns {'network'|'stripe_config'|'checkout_conflict'|'unknown'}
 */
export function classifyCheckoutError(message) {
  const msg = String(message || '').toLowerCase();
  if (
    msg.includes('timeout') || msg.includes('network')
    || msg.includes('fetch') || msg.includes('parse response')
  ) {
    return 'network';
  }
  if (msg.includes('not configured') || msg.includes('no checkout url')) {
    return 'stripe_config';
  }
  if (msg.includes('already') || msg.includes('in progress')) {
    return 'checkout_conflict';
  }
  return 'unknown';
}

/**
 * @typedef {Object} AnalyticsConfig
 * @property {string} [userId] - Anonymous user ID
 * @property {boolean} [isGuest] - Whether user is a guest
 * @property {string} [subscriptionTier] - GA4 `subscription_tier` value:
 *   'free' (Basic — kept for funnel continuity) or 'premium' (see
 *   `resolveAnalyticsTier`)
 * @property {boolean} [onboardingComplete] - Whether user completed onboarding
 */

export class AnalyticsService {
  /**
   * @param {Object} eventBus - Event bus for component communication
   * @param {AnalyticsConfig} [config] - Configuration options
   */
  constructor(eventBus, config = {}) {
    this.eventBus = eventBus;
    this.config = { ...config };

    this.initialized = false;
    this.sessionStart = null;
    this.messageCount = 0;
    this.currentMode = null;

    // Bind event handlers for proper cleanup
    this.boundHandlers = {
      sessionStarted: this.handleSessionStarted.bind(this),
      sessionEnded: this.handleSessionEnded.bind(this),
      messageSend: this.handleMessageSend.bind(this),
      modeChanged: this.handleModeChanged.bind(this),
      modeRestored: this.handleModeRestored.bind(this),
      modeCleared: this.handleModeCleared.bind(this),
      questionnaireCompleted: this.handleQuestionnaireCompleted.bind(this),
      orientationCompleted: this.handleOrientationCompleted.bind(this),
      accountCreated: this.handleAccountCreated.bind(this),
      upgradeShown: this.handleUpgradeShown.bind(this),
      upgradeClicked: this.handleUpgradeClicked.bind(this),
      quotaWarning: this.handleQuotaWarning.bind(this),
      quotaExhausted: this.handleQuotaExhausted.bind(this),
      checkoutStarted: this.handleCheckoutStarted.bind(this),
      checkoutFailed: this.handleCheckoutFailed.bind(this),
      cancelPortalStarted: this.handleCancelPortalStarted.bind(this),
      cancelConfirmed: this.handleCancelConfirmed.bind(this),
      reactivateClicked: this.handleReactivateClicked.bind(this),
      subscriptionActivated: this.handleSubscriptionActivated.bind(this),
      subscriptionStatus: this.handleSubscriptionStatus.bind(this),
      chatError: this.handleChatError.bind(this)
    };
  }

  /**
   * Initialize the analytics service
   * Sets up event listeners and user properties
   */
  initialize() {
    if (this.initialized) {
      console.warn('[AnalyticsService] Already initialized');
      return;
    }

    // Check if GA4 is available
    if (!this.isAvailable()) {
      console.warn('[AnalyticsService] GA4 not available - tracking disabled');
      return;
    }

    // Set up event listeners
    this.setupEventListeners();

    // Set initial user properties
    this.setUserProperties();

    this.initialized = true;
    console.log('[AnalyticsService] Initialized');
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    if (!this.eventBus) return;

    // Remove all event listeners
    this.eventBus.off('session:started', this.boundHandlers.sessionStarted);
    this.eventBus.off('session:ended', this.boundHandlers.sessionEnded);
    this.eventBus.off('message:send', this.boundHandlers.messageSend);
    this.eventBus.off('mode:changed', this.boundHandlers.modeChanged);
    this.eventBus.off('mode:restored', this.boundHandlers.modeRestored);
    this.eventBus.off('mode:cleared', this.boundHandlers.modeCleared);
    this.eventBus.off('questionnaire:completed', this.boundHandlers.questionnaireCompleted);
    this.eventBus.off('orientation:completed', this.boundHandlers.orientationCompleted);
    this.eventBus.off('auth:account-created', this.boundHandlers.accountCreated);
    this.eventBus.off('upgrade:shown', this.boundHandlers.upgradeShown);
    this.eventBus.off('upgrade:clicked', this.boundHandlers.upgradeClicked);
    this.eventBus.off('quota:warning', this.boundHandlers.quotaWarning);
    this.eventBus.off('quota:exhausted', this.boundHandlers.quotaExhausted);
    this.eventBus.off('upgrade:checkout-started', this.boundHandlers.checkoutStarted);
    this.eventBus.off('upgrade:checkout-failed', this.boundHandlers.checkoutFailed);
    this.eventBus.off('subscription:cancel-portal-started', this.boundHandlers.cancelPortalStarted);
    this.eventBus.off('subscription:cancel-confirmed', this.boundHandlers.cancelConfirmed);
    this.eventBus.off('subscription:reactivate-clicked', this.boundHandlers.reactivateClicked);
    this.eventBus.off('subscription:activated', this.boundHandlers.subscriptionActivated);
    this.eventBus.off('subscription:status', this.boundHandlers.subscriptionStatus);
    this.eventBus.off('chat:error', this.boundHandlers.chatError);

    this.initialized = false;
    console.log('[AnalyticsService] Destroyed');
  }

  // ============================================================
  // CORE TRACKING METHODS
  // ============================================================

  /**
   * Check if GA4 is available and loaded
   * @returns {boolean}
   */
  isAvailable() {
    return typeof window.gtag === 'function';
  }

  /**
   * Track a GA4 event
   * @param {string} eventName - GA4 event name
   * @param {Object} [params] - Event parameters (will be sanitized)
   */
  trackEvent(eventName, params = {}) {
    if (!this.isAvailable()) {
      console.debug('[AnalyticsService] GA4 not available, skipping event:', eventName);
      return;
    }

    // Sanitize params to ensure no PII or content leaks
    const safeParams = this.sanitizeParams(params);

    try {
      window.gtag('event', eventName, safeParams);
      console.debug('[AnalyticsService] Event tracked:', eventName, safeParams);
    } catch (error) {
      console.error('[AnalyticsService] Failed to track event:', eventName, error);
    }
  }

  /**
   * Track a page view (for SPA navigation)
   * @param {string} path - Page path
   */
  trackPageView(path) {
    if (!this.isAvailable()) return;

    try {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title
      });
      console.debug('[AnalyticsService] Page view tracked:', path);
    } catch (error) {
      console.error('[AnalyticsService] Failed to track page view:', error);
    }
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties() {
    if (!this.isAvailable()) return;

    try {
      window.gtag('set', 'user_properties', {
        user_type: this.config.isGuest ? 'guest' : 'authenticated',
        subscription_tier: this.config.subscriptionTier || 'free',
        onboarding_complete: this.config.onboardingComplete ? 'yes' : 'no'
      });
      console.debug('[AnalyticsService] User properties set');
    } catch (error) {
      console.error('[AnalyticsService] Failed to set user properties:', error);
    }
  }

  /**
   * Update user data after login or subscription change
   * @param {Object} user - User object
   */
  setUser(user) {
    if (!user) return;

    this.config.userId = user.id;
    this.config.isGuest = user.isGuest || false;
    this.config.subscriptionTier = resolveAnalyticsTier(user);
    this.config.onboardingComplete = user.onboardingStep === 'complete';

    this.setUserProperties();
  }

  // ============================================================
  // PRIVACY HELPERS
  // ============================================================

  /**
   * Sanitize parameters to remove any potential PII or content
   * @param {Object} params - Raw parameters
   * @returns {Object} Sanitized parameters
   */
  sanitizeParams(params) {
    const safe = {};

    // Allowlist of safe parameter names
    const allowedParams = [
      'mode', 'from_mode', 'to_mode',
      'message_count', 'duration_seconds',
      'path_id', 'questionnaire_id',
      'error_type', 'component', 'trigger', 'location',
      'tier', 'method', 'from_guest', 'steps_completed',
      'privacy_mode', 'message_type', 'page_path', 'page_title',
      'surface', 'trial', 'partner_rate'
    ];

    for (const key of allowedParams) {
      if (params[key] !== undefined) {
        safe[key] = params[key];
      }
    }

    return safe;
  }

  // ============================================================
  // EVENT LISTENERS
  // ============================================================

  /**
   * Set up eventBus listeners
   */
  setupEventListeners() {
    if (!this.eventBus) return;

    // Tier 1: Core Engagement
    this.eventBus.on('session:started', this.boundHandlers.sessionStarted);
    this.eventBus.on('session:ended', this.boundHandlers.sessionEnded);
    this.eventBus.on('message:send', this.boundHandlers.messageSend);
    this.eventBus.on('mode:changed', this.boundHandlers.modeChanged);
    this.eventBus.on('mode:restored', this.boundHandlers.modeRestored);
    this.eventBus.on('mode:cleared', this.boundHandlers.modeCleared);

    // Tier 2: Conversion Funnel
    this.eventBus.on('questionnaire:completed', this.boundHandlers.questionnaireCompleted);
    this.eventBus.on('orientation:completed', this.boundHandlers.orientationCompleted);
    this.eventBus.on('auth:account-created', this.boundHandlers.accountCreated);

    // Tier 3: Upgrade Funnel
    this.eventBus.on('upgrade:shown', this.boundHandlers.upgradeShown);
    this.eventBus.on('upgrade:clicked', this.boundHandlers.upgradeClicked);
    this.eventBus.on('quota:warning', this.boundHandlers.quotaWarning);
    this.eventBus.on('quota:exhausted', this.boundHandlers.quotaExhausted);
    this.eventBus.on('upgrade:checkout-started', this.boundHandlers.checkoutStarted);
    this.eventBus.on('upgrade:checkout-failed', this.boundHandlers.checkoutFailed);
    this.eventBus.on('subscription:cancel-portal-started', this.boundHandlers.cancelPortalStarted);
    this.eventBus.on('subscription:cancel-confirmed', this.boundHandlers.cancelConfirmed);
    this.eventBus.on('subscription:reactivate-clicked', this.boundHandlers.reactivateClicked);
    this.eventBus.on('subscription:activated', this.boundHandlers.subscriptionActivated);
    this.eventBus.on('subscription:status', this.boundHandlers.subscriptionStatus);

    // Tier 4: Health Metrics
    this.eventBus.on('chat:error', this.boundHandlers.chatError);
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  /**
   * Handle session started event
   * @param {Object} data - Session data
   */
  handleSessionStarted(data) {
    this.sessionStart = Date.now();
    this.messageCount = 0;
    this.currentMode = data?.mode || 'unknown';

    this.trackEvent('session_started', {
      mode: this.currentMode,
      privacy_mode: data?.privacyMode ? 'enabled' : 'disabled'
    });
  }

  /**
   * Handle session ended event
   * @param {Object} data - Session data
   */
  handleSessionEnded(data) {
    const durationSeconds = this.sessionStart
      ? Math.round((Date.now() - this.sessionStart) / 1000)
      : 0;

    this.trackEvent('session_ended', {
      mode: this.currentMode || data?.mode || 'unknown',
      message_count: this.messageCount,
      duration_seconds: durationSeconds
    });

    // Reset session tracking
    this.sessionStart = null;
    this.messageCount = 0;
  }

  /**
   * Handle message send event
   * CRITICAL: Only track metadata, NEVER message content
   * @param {Object} data - Message data
   */
  handleMessageSend(data) {
    this.messageCount++;

    // PRIVACY: Only track mode and type, never content
    this.trackEvent('message_sent', {
      mode: this.currentMode || 'unknown',
      message_type: data?.type || 'user'
    });
  }

  /**
   * Handle mode changed event
   * @param {Object} data - Mode change data
   */
  handleModeChanged(data) {
    const fromMode = this.currentMode || 'dashboard';
    const toMode = data?.mode?.name || data?.mode || 'unknown';

    this.trackEvent('mode_switched', {
      from_mode: fromMode,
      to_mode: toMode
    });

    this.currentMode = toMode;
  }

  /**
   * Handle mode restored event
   * @param {Object} data - Mode restore data
   */
  handleModeRestored(data) {
    const toMode = data?.mode?.name || data?.mode || 'unknown';
    this.currentMode = toMode;

    this.trackEvent('mode_switched', {
      from_mode: 'dashboard',
      to_mode: toMode
    });
  }

  /**
   * Handle mode cleared event (dashboard return).
   * Resets local mode tracking so the next mode_switched event reports
   * `from_mode: 'dashboard'` instead of the stale prior mode.
   */
  handleModeCleared() {
    this.currentMode = null;
  }

  /**
   * Handle questionnaire completed event
   * CRITICAL: Only track path_id, NEVER answers or scores
   * @param {Object} data - Questionnaire completion data
   */
  handleQuestionnaireCompleted(data) {
    const pathId = data?.pathId || 'unknown';
    // PRIVACY: Only track identifier, never responses
    this.trackEvent('questionnaire_completed', {
      path_id: pathId,
      questionnaire_id: data?.questionnaireId || 'unknown'
    });
  }

  /**
   * Handle orientation completed event
   * @param {Object} data - Orientation completion data
   */
  handleOrientationCompleted(data) {
    const durationSeconds = data?.duration
      ? Math.round(data.duration / 1000)
      : 0;

    this.trackEvent('orientation_completed', {
      duration_seconds: durationSeconds,
      steps_completed: data?.stepsCompleted || 0
    });
  }

  /**
   * Handle account created event
   * @param {Object} data - Account creation data
   */
  handleAccountCreated(data) {
    this.trackEvent('account_created', {
      method: data?.method || 'email',
      from_guest: data?.fromConversion ? 'yes' : 'no'
    });

    // Update user properties after account creation
    if (data?.user) {
      this.setUser(data.user);
    }
  }

  /**
   * Handle upgrade prompt shown event
   * @param {Object} data - Upgrade prompt data
   */
  handleUpgradeShown(data) {
    this.trackEvent('upgrade_prompt_shown', {
      component: data?.component || 'unknown',
      trigger: data?.trigger || 'unknown'
    });
  }

  /**
   * Handle upgrade clicked event
   * @param {Object} data - Upgrade click data
   */
  handleUpgradeClicked(data) {
    this.trackEvent('upgrade_clicked', {
      component: data?.component || 'unknown',
      location: data?.location || 'unknown'
    });
  }

  /**
   * Handle a quota-warning transition (UsageService dedupes per UTC day).
   * @param {Object} data - `{surface: 'chat'|'dashboard'}`
   */
  handleQuotaWarning(data) {
    this.trackEvent('quota_warning', {
      surface: data?.surface || 'unknown'
    });
  }

  /**
   * Handle a quota-exhausted transition (UsageService dedupes per UTC day).
   * @param {Object} data - `{surface: 'chat'|'dashboard'}`
   */
  handleQuotaExhausted(data) {
    this.trackEvent('quota_exhausted', {
      surface: data?.surface || 'unknown'
    });
  }

  /**
   * Handle checkout started — the Stripe redirect is about to happen.
   * The payload's `priceId` is deliberately not forwarded (not allowlisted).
   */
  handleCheckoutStarted() {
    this.trackEvent('checkout_started');
  }

  /**
   * Handle checkout failed. The emitter's payload carries the raw error
   * message; only the bounded classification leaves the browser.
   * @param {Object} data - `{error: string}` (raw message, never forwarded)
   */
  handleCheckoutFailed(data) {
    this.trackEvent('checkout_failed', {
      error_type: classifyCheckoutError(data?.error)
    });
  }

  /**
   * Handle cancel-portal opened — cancellation INTENT (the user confirmed
   * in-app and is being handed to Stripe's cancel flow).
   */
  handleCancelPortalStarted() {
    this.trackEvent('cancel_portal_started');
  }

  /**
   * Handle observed cancellation — a status read derived the `canceling`
   * state for a subscription that wasn't previously canceling
   * (SubscriptionSection dedupes; see its `_noteCancelObservation`).
   */
  handleCancelConfirmed() {
    this.trackEvent('cancel_confirmed');
  }

  /**
   * Handle the reactivate action on the lapsed-subscription card.
   */
  handleReactivateClicked() {
    this.trackEvent('reactivate_clicked');
  }

  /**
   * Adopt the shared subscription snapshot as the `subscription_tier` user
   * property — the refetched snapshot tracks upgrades the boot-time identity
   * stamp never sees.
   *
   * @param {Object|null} status - Status payload from `subscription:status`
   */
  handleSubscriptionStatus(status) {
    this.config.subscriptionTier = analyticsTierFromStatus(status);
    this.setUserProperties();
  }

  /**
   * Handle subscription activated event. The router enriches the emission
   * with `{trial, partner_rate}` derived from the polled activation status;
   * both are forwarded only when present so a legacy payload sends neither.
   * @param {Object} data - Subscription data
   */
  handleSubscriptionActivated(data) {
    const params = { tier: data?.tier || 'premium' };
    if (typeof data?.trial === 'boolean') params.trial = data.trial;
    if (typeof data?.partner_rate === 'boolean') params.partner_rate = data.partner_rate;
    this.trackEvent('subscription_activated', params);

    // Update user properties
    this.config.subscriptionTier = data?.tier || 'premium';
    this.setUserProperties();
  }

  /**
   * Handle chat error event
   * CRITICAL: Only track error type, NEVER error details or messages
   * @param {Object} data - Error data
   */
  handleChatError(data) {
    // PRIVACY: Only track error category, never details
    // Extract type from various possible formats:
    // - data.type (explicit type)
    // - data.code (error code)
    // - data.error?.code (nested error code)
    // - Classify from data.message (e.g., 'Stream processing error' -> 'stream_error')
    let errorType = data?.type || data?.code || data?.error?.code;

    if (!errorType && data?.message) {
      // Classify common error messages into safe categories
      const msg = data.message.toLowerCase();
      if (msg.includes('stream')) errorType = 'stream_error';
      else if (msg.includes('timeout')) errorType = 'timeout_error';
      else if (msg.includes('network')) errorType = 'network_error';
      else errorType = 'processing_error';
    }

    this.trackEvent('chat_error', {
      error_type: errorType || 'unknown',
      mode: this.currentMode || 'unknown'
    });
  }
}
